import React from 'react';
import styled from 'styled-components';

import { use7daysPortfolioPnlChartQuery } from 'api/queries/walletPositionsPnLQueries';
import { useWalletWithPositionsQuery } from 'api/queries/walletPositionsQueries';
import MaterialSymbol from 'components/basic/icons/MaterialSymbol';
import { statBoxes } from 'components/basic/panels/StatsBoxes';
import Tooltip from 'components/basic/tooltip/Tooltip';
import PortfolioChart from 'components/chart/portfolio/PortfolioChart';
import {
  balanceDescription,
  freeCollateralDescription,
  leverageDescription,
  marginRationDescription,
} from 'components/wallet/walletSharedTooltips';
import {
  formatNumber,
  formatPercentage,
  formatUsd,
  formatUsdWithSign,
} from 'lib/display/formatting';
import { staticDispatch, useSelector } from 'reduxState/store';
import { toggleWalletMobileHeaderStats } from 'reduxState/walletSection/walletSectionReducer';

import WallletLast24hStat from './WallletLast24hStat';

const { Wrap, BoxLarge, Box, Title, Value } = statBoxes;

const PortfolioWrap = styled.div`
  width: 100%;
  border-radius: 2px;
  background: var(--elevation-light);
  margin: 0;
`;

const MobileFloatedArrow = styled.div`
  float: right;
  padding-right: 2px;
`;

const DesktopOnlyTooltip = ({ text }: { text: string }) => {
  const isMobile = useSelector(state => state.screen.isMobile);
  return isMobile ? null : (
    <>
      {' '}
      <Tooltip text={text}>
        <MaterialSymbol
          icon="info"
          fontSize="inherit"
          // onClick={link ? () => redirectTo(link) : undefined}
        />
      </Tooltip>
    </>
  );
};

function WallletStats() {
  const isMobile = useSelector(state => state.screen.isMobile);
  const walletAddress = useSelector(state => state.wallet.walletAddress);
  const mobileHeaderStatsExpanded = useSelector(
    state => state.walletSection.mobileHeaderStatsExpanded
  );
  const { data: walletWithPositions, isLoading } =
    useWalletWithPositionsQuery();

  const { data: portfolioData } = use7daysPortfolioPnlChartQuery({
    walletAddress,
    isVault: false,
  });

  /** We are loading wallet data */
  const loadingForWallet = isLoading && walletAddress;

  return (
    <>
      <Wrap>
        <BoxLarge
          onClick={
            isMobile
              ? () => staticDispatch(toggleWalletMobileHeaderStats())
              : undefined
          }
        >
          <Title>
            Balance
            <DesktopOnlyTooltip text={balanceDescription} />
          </Title>
          <Value>
            {loadingForWallet
              ? null
              : walletWithPositions?.equity
              ? formatUsdWithSign(walletWithPositions.equity)
              : '-'}
            {isMobile && (
              <MobileFloatedArrow>
                {mobileHeaderStatsExpanded ? (
                  <MaterialSymbol
                    icon="arrow_drop_up"
                    color="var(--gray4c)"
                    fontSize="large"
                    rounded
                  />
                ) : (
                  <MaterialSymbol
                    icon="arrow_drop_down"
                    color="var(--gray4c)"
                    fontSize="large"
                    rounded
                  />
                )}
              </MobileFloatedArrow>
            )}
          </Value>
          <WallletLast24hStat />
        </BoxLarge>
        {(mobileHeaderStatsExpanded || !isMobile) && (
          <>
            <Box>
              <Title>
                Free Collateral{' '}
                <DesktopOnlyTooltip text={freeCollateralDescription} />
              </Title>
              <Value>
                {loadingForWallet ? null : walletWithPositions?.freeCollateral ? (
                  <>${formatUsd(walletWithPositions.freeCollateral)}</>
                ) : (
                  '-'
                )}
              </Value>
            </Box>
            <Box>
              <Title>
                Margin Ratio
                <DesktopOnlyTooltip text={marginRationDescription} />
              </Title>
              <Value>
                {loadingForWallet
                  ? null
                  : walletWithPositions?.marginRatio
                  ? `${formatPercentage(
                      walletWithPositions.marginRatio,
                      true
                    )}%`
                  : '-'}
              </Value>
            </Box>
            <Box>
              <Title>
                Leverage
                <DesktopOnlyTooltip text={leverageDescription} />
              </Title>
              <Value>
                {loadingForWallet
                  ? null
                  : walletWithPositions?.leverage
                  ? formatNumber(walletWithPositions.leverage, {
                      fixedDecimals: 2,
                    })
                  : '-'}
              </Value>
            </Box>
          </>
        )}
      </Wrap>

      <PortfolioWrap>
        <PortfolioChart
          lastEquity={walletWithPositions?.equity}
          portfolioData={portfolioData}
        />
      </PortfolioWrap>
    </>
  );
}

export default WallletStats;
